import { computed, type Ref } from 'vue'
import type {
  JobFilter,
  JobFilterComparison,
  JobFilterProperty,
  JobFilterType,
  MultiselectOption,
} from '@ironsight/types'
import { priorities } from '@/utils/constants'
import moment from 'moment'
import { SUPPORTED_JOB_FILTER_OPERATIONS } from './useJobFilterProperties'

export const MAX_LABEL_LENGTH = 40
export const LOCATION_FILTER_IDS = [
  'LocationId',
  'PickupLocationId',
  'DestinationLocationId',
  'ConfirmedPickupLocationId',
  'ConfirmedDestinationLocationId',
]

export function isLocationFilter(filter: JobFilter): boolean {
  return LOCATION_FILTER_IDS.includes(filter.property) || filter.lookupEntity === 'Location'
}
export const HAS_VALUE_SENTINEL = '__HAS_VALUE__'
export const SELECT_ALL_SENTINEL = '__SELECT_ALL__'

export type SelectAllScope = {
  searchText?: string
  locationTypeId?: number
  locationTypeLabel?: string
  parentLocationId?: number
  parentLocationLabel?: string
  parentLocationAncestorIds?: number[]
}

export type SelectAllOption = MultiselectOption & {
  value: typeof SELECT_ALL_SENTINEL
  searchText?: string
  excludedSearchTexts?: string[]
  scopes?: SelectAllScope[]
  excludedScopes?: SelectAllScope[]
}

export type SelectAllIncludedOption = MultiselectOption & {
  selectionMode: 'include'
}

/**
 * Returns true when the filter value uses the select-all-with-exclusions model.
 * The sentinel MultiselectOption `{ value: SELECT_ALL_SENTINEL }` is always at index 0;
 * any subsequent entries represent excluded items.
 */
export function isSelectAllValue(
  value: unknown,
): value is [MultiselectOption, ...MultiselectOption[]] {
  if (!Array.isArray(value) || value.length === 0) return false
  const first = value[0] as MultiselectOption | undefined
  return first?.value === SELECT_ALL_SENTINEL
}

/**
 * Extracts the excluded MultiselectOption entries from a select-all value array.
 * Returns an empty array when the value is not in select-all mode.
 */
export function getSelectAllExclusions(value: unknown): MultiselectOption[] {
  if (!isSelectAllValue(value)) return []
  return (value as MultiselectOption[]).slice(1).filter((option) => !isSelectAllInclusion(option))
}

export function isSelectAllInclusion(option: MultiselectOption): option is SelectAllIncludedOption {
  return (option as Partial<SelectAllIncludedOption>).selectionMode === 'include'
}

export function getSelectAllInclusions(value: unknown): MultiselectOption[] {
  if (!isSelectAllValue(value)) return []
  return (value as MultiselectOption[])
    .slice(1)
    .filter(isSelectAllInclusion)
    .map((option) => {
      const includedOption = { ...option } as Partial<SelectAllIncludedOption>
      delete includedOption.selectionMode
      return includedOption as MultiselectOption
    })
}

export function getSelectAllSearchText(value: unknown): string | null {
  if (!isSelectAllValue(value)) return null

  const searchText = (value[0] as Partial<SelectAllOption>).searchText
  if (typeof searchText !== 'string') return null

  const normalizedSearchText = searchText.trim()
  return normalizedSearchText || null
}

export function getSelectAllExcludedSearchTexts(value: unknown): string[] {
  if (!isSelectAllValue(value)) return []

  const excludedSearchTexts = (value[0] as Partial<SelectAllOption>).excludedSearchTexts
  if (!Array.isArray(excludedSearchTexts)) return []

  return Array.from(
    new Set(
      excludedSearchTexts
        .filter((searchText): searchText is string => typeof searchText === 'string')
        .map((searchText) => searchText.trim())
        .filter(Boolean),
    ),
  )
}

function normalizeSelectAllScope(scope: unknown): SelectAllScope | null {
  if (!scope || typeof scope !== 'object') return null

  const record = scope as Record<string, unknown>
  const searchText = typeof record.searchText === 'string' ? record.searchText.trim() : ''
  const locationTypeId =
    typeof record.locationTypeId === 'number' ? record.locationTypeId : undefined
  const locationTypeLabel =
    typeof record.locationTypeLabel === 'string' ? record.locationTypeLabel.trim() : ''
  const parentLocationId =
    typeof record.parentLocationId === 'number' ? record.parentLocationId : undefined
  const parentLocationLabel =
    typeof record.parentLocationLabel === 'string' ? record.parentLocationLabel.trim() : ''
  const parentLocationAncestorIds = Array.isArray(record.parentLocationAncestorIds)
    ? record.parentLocationAncestorIds.filter(
        (ancestorId): ancestorId is number => typeof ancestorId === 'number',
      )
    : []

  if (!searchText && typeof locationTypeId !== 'number' && typeof parentLocationId !== 'number') {
    return null
  }

  return {
    ...(searchText ? { searchText } : {}),
    ...(typeof locationTypeId === 'number' ? { locationTypeId } : {}),
    ...(locationTypeLabel ? { locationTypeLabel } : {}),
    ...(typeof parentLocationId === 'number' ? { parentLocationId } : {}),
    ...(parentLocationLabel ? { parentLocationLabel } : {}),
    ...(parentLocationAncestorIds.length > 0 ? { parentLocationAncestorIds } : {}),
  }
}

function normalizeSelectAllScopes(scopes: SelectAllScope[]): SelectAllScope[] {
  const seen = new Set<string>()
  const normalizedScopes: SelectAllScope[] = []

  for (const scope of scopes) {
    const normalizedScope = normalizeSelectAllScope(scope)
    if (!normalizedScope) continue

    const key = [
      normalizedScope.locationTypeId ?? '',
      normalizedScope.parentLocationId ?? '',
      normalizedScope.parentLocationAncestorIds?.join(',') ?? '',
      normalizedScope.searchText?.toLowerCase() ?? '',
    ].join('|')
    if (seen.has(key)) continue

    seen.add(key)
    normalizedScopes.push(normalizedScope)
  }

  return normalizedScopes
}

export function getSelectAllScopes(value: unknown): SelectAllScope[] {
  if (!isSelectAllValue(value)) return []

  const sentinel = value[0] as Partial<SelectAllOption>
  if (!Array.isArray(sentinel.scopes)) return []

  return normalizeSelectAllScopes(sentinel.scopes)
}

export function getSelectAllExcludedScopes(value: unknown): SelectAllScope[] {
  if (!isSelectAllValue(value)) return []

  const sentinel = value[0] as Partial<SelectAllOption>
  if (!Array.isArray(sentinel.excludedScopes)) return []

  return normalizeSelectAllScopes(sentinel.excludedScopes)
}

function formatSelectAllScope(scope: SelectAllScope): string {
  const locationTypeLabel =
    scope.locationTypeLabel ||
    (typeof scope.locationTypeId === 'number' ? `location type ${scope.locationTypeId}` : '')
  const parentLocationLabel =
    scope.parentLocationLabel ||
    (typeof scope.parentLocationId === 'number' ? `location ${scope.parentLocationId}` : '')
  const baseLabel = [locationTypeLabel, parentLocationLabel ? `within ${parentLocationLabel}` : '']
    .filter(Boolean)
    .join(' ')

  if (baseLabel && scope.searchText) {
    return `${baseLabel} matching "${scope.searchText}"`
  }

  if (baseLabel) return baseLabel
  if (scope.searchText) return `matching "${scope.searchText}"`

  return 'All'
}

function formatSelectAllScopeList(scopes: SelectAllScope[]): string {
  return `All ${scopes.map(formatSelectAllScope).join(', ')}`
}

/**
 * Builds a select-all value array: sentinel first, followed by excluded options.
 */
export function makeSelectAllValue(
  exclusions: MultiselectOption[] = [],
  searchText?: string | null,
  inclusions: MultiselectOption[] = [],
  excludedSearchTexts: string[] = [],
  scopes: SelectAllScope[] = [],
  excludedScopes: SelectAllScope[] = [],
): MultiselectOption[] {
  const normalizedSearchText = searchText?.trim()
  const normalizedExcludedSearchTexts = Array.from(
    new Set(excludedSearchTexts.map((text) => text.trim()).filter(Boolean)),
  )
  const normalizedScopes = normalizeSelectAllScopes(scopes)
  const normalizedExcludedScopes = normalizeSelectAllScopes(excludedScopes)
  const sentinel: SelectAllOption = {
    label:
      normalizedScopes.length > 0
        ? formatSelectAllScopeList(normalizedScopes)
        : normalizedSearchText
          ? `All matching "${normalizedSearchText}"`
          : 'All',
    value: SELECT_ALL_SENTINEL,
  }

  if (normalizedSearchText) {
    sentinel.searchText = normalizedSearchText
  }

  if (normalizedExcludedSearchTexts.length > 0) {
    sentinel.excludedSearchTexts = normalizedExcludedSearchTexts
  }

  if (normalizedScopes.length > 0) {
    sentinel.scopes = normalizedScopes
  }

  if (normalizedExcludedScopes.length > 0) {
    sentinel.excludedScopes = normalizedExcludedScopes
  }

  const includedOptions: SelectAllIncludedOption[] = inclusions.map((option) => ({
    ...option,
    selectionMode: 'include',
  }))

  return [sentinel, ...exclusions, ...includedOptions]
}

function getSelectAllLabelPrefix(value: unknown): string {
  const scopes = getSelectAllScopes(value)
  if (scopes.length > 0) return formatSelectAllScopeList(scopes)

  const searchText = getSelectAllSearchText(value)
  return searchText ? `All matching "${searchText}"` : 'All'
}

function buildSelectAllLabel(value: unknown): string {
  const labelPrefix = getSelectAllLabelPrefix(value)
  const exclusions = getSelectAllExclusions(value)
  const excludedSearchTexts = getSelectAllExcludedSearchTexts(value)
  const excludedScopes = getSelectAllExcludedScopes(value)
  const inclusions = getSelectAllInclusions(value)
  const segments = [labelPrefix]

  if (excludedScopes.length > 0) {
    segments.push(`except ${excludedScopes.map(formatSelectAllScope).join(', ')}`)
  }

  if (excludedSearchTexts.length > 0) {
    segments.push(`except matching ${excludedSearchTexts.map((text) => `"${text}"`).join(', ')}`)
  }

  if (exclusions.length > 0) {
    const labels = exclusions.map((o) => o.label).filter(Boolean)
    segments.push(`except ${labels.join(', ')}`)
  }

  if (inclusions.length > 0) {
    const labels = inclusions.map((o) => o.label).filter(Boolean)
    segments.push(`plus ${labels.join(', ')}`)
  }

  return segments.join(' ')
}

function getOperationLabel(filterType: JobFilterType, comparison: JobFilterComparison): string {
  const operation = SUPPORTED_JOB_FILTER_OPERATIONS.find(
    (op) => op.filterType === filterType && op.comparison === comparison,
  )
  if (!operation) return ''
  return operation.chipLabel ? operation.chipLabel.toLowerCase() : operation.label.toLowerCase()
}

export function buildLabelStart(
  filter: JobFilter,
  property: JobFilterProperty | null | undefined,
): string {
  return property?.label || filter.label || filter.property
}

export function buildLabelMiddle(filter: JobFilter): string {
  const { comparison, type, value, property } = filter

  // Select-all mode: always use " is "
  if (isSelectAllValue(value)) {
    return ' is '
  }

  if (
    comparison === 'In' &&
    (property === 'Tags' ||
      (property === 'Followers' && (!value || (Array.isArray(value) && value.length < 2))))
  ) {
    return ' include '
  }

  if (comparison === 'In' && Array.isArray(value)) {
    if (value.length === 0) return ''
    if (value.length === 1) return ' is '
  }

  if (comparison && type) {
    const label = getOperationLabel(type, comparison)
    return label ? ` ${label} ` : ''
  }

  return ''
}

export function buildLabelEnd(filter: JobFilter): string {
  const { property, type: filterType, value } = filter

  // Select-all mode: "All", "All except X, Y", or search-scoped variants with explicit additions.
  if (isSelectAllValue(value)) {
    return buildSelectAllLabel(value)
  }

  if (isLocationFilter(filter)) return ''

  if (filterType === 'DateTime') {
    if (Array.isArray(value) && value.length === 2) {
      const [start, end] = value as [Date | null, Date | null]
      const startMoment = start ? moment(start) : null
      const endMoment = end ? moment(end) : null
      if (startMoment && endMoment) {
        if (startMoment.isSame(endMoment, 'day')) {
          return startMoment.format('MMM D')
        }
        return `${startMoment.format('MMM D')} - ${endMoment.format('MMM D')}`
      }
    }
    return ''
  }

  if (Array.isArray(value) && value.length === 1 && value[0].value === '') {
    return 'empty'
  }

  if (Array.isArray(value) && value.length === 1 && value[0].value === HAS_VALUE_SENTINEL) {
    return 'not empty'
  }

  if (property === 'Priority') {
    if (Array.isArray(value)) {
      return (
        value
          .map((v) => priorities.find((p) => p.id === v)?.name)
          .filter(Boolean)
          .join(', ') || ''
      )
    }
    return priorities.find((p) => p.id === value)?.name ?? ''
  }

  if (filterType === 'StringLookup' || filterType === 'IntLookup') {
    if (Array.isArray(value)) {
      if (value.length === 1) return value[0].label || ''
      return value.map((option) => option.label).join(', ') || ''
    }
  }

  if (Array.isArray(value)) {
    const [start, end] = value
    return `${start} - ${end}`
  }

  if (value instanceof Date) return value.toISOString()

  if (['Integer', 'Decimal', 'Double'].includes(filterType)) {
    const numValue = typeof value === 'string' ? parseFloat(value) : (value as number)
    if (!isNaN(numValue)) {
      switch (filterType) {
        case 'Integer':
          return numValue.toFixed(0)
        case 'Decimal':
          return numValue.toFixed(2)
        case 'Double':
          return numValue.toFixed(6)
      }
    }
    return String(value)
  }

  if (filterType === 'Boolean') return value ? 'Yes' : 'No'

  return String(value)
}

export function buildFullLabel(
  filter: JobFilter,
  property: JobFilterProperty | null | undefined,
): string {
  const start = buildLabelStart(filter, property)
  const end = buildLabelEnd(filter)

  if (filter.type === 'DateTime' && filter.comparison === 'Between') {
    return `${start} ${end}`
  }

  return `${start}${buildLabelMiddle(filter)}${end}`
}

export function buildTruncatedLabel(
  filter: JobFilter,
  property: JobFilterProperty | null | undefined,
): string {
  const start = buildLabelStart(filter, property)
  const middle = buildLabelMiddle(filter)
  const end = buildLabelEnd(filter)

  // Select-all mode: "Label is All" or search-scoped variants with exclusions/additions.
  if (isSelectAllValue(filter.value)) {
    const labelPrefix = getSelectAllLabelPrefix(filter.value)
    const exclusions = getSelectAllExclusions(filter.value)
    const excludedSearchTexts = getSelectAllExcludedSearchTexts(filter.value)
    const excludedScopes = getSelectAllExcludedScopes(filter.value)
    const inclusions = getSelectAllInclusions(filter.value)
    const exclusionCount = exclusions.length + excludedSearchTexts.length + excludedScopes.length
    if (exclusionCount === 0 && inclusions.length === 0) return `${start} is ${labelPrefix}`

    const full = `${start} is ${buildSelectAllLabel(filter.value)}`
    if (full.length <= MAX_LABEL_LENGTH) {
      return full
    }

    const segments = [`${start} is ${labelPrefix}`]
    if (exclusionCount > 0) {
      segments.push(`except (${exclusionCount})`)
    }
    if (inclusions.length > 0) {
      segments.push(`plus (${inclusions.length})`)
    }
    const counted = segments.join(' ')

    if (counted.length <= MAX_LABEL_LENGTH) return counted

    const searchText = getSelectAllSearchText(filter.value)
    const compactPrefix = searchText ? `"${searchText}"` : labelPrefix
    const compactSegments = [`${start} is ${compactPrefix}`]
    if (exclusionCount > 0) {
      compactSegments.push(`except (${exclusionCount})`)
    }
    if (inclusions.length > 0) {
      compactSegments.push(`plus (${inclusions.length})`)
    }
    const compact = compactSegments.join(' ')

    if (compact.length <= MAX_LABEL_LENGTH) return compact

    if (exclusionCount > 0) return `${start} is ${labelPrefix} except (${exclusionCount})`
    return `${start} is ${labelPrefix} plus (${inclusions.length})`
  }

  // Location filters: show count
  if (isLocationFilter(filter) && Array.isArray(filter.value)) {
    return `${start} (${filter.value.length})`
  }

  // DateTime between: "Label date range"
  if (filter.type === 'DateTime' && filter.comparison === 'Between') {
    return `${start} ${end}`
  }

  const fullLabel = `${start}${middle}${end}`

  // Truncation for "In" with multiple values
  if (filter.comparison === 'In' && Array.isArray(filter.value) && filter.value.length > 1) {
    const indicator = `(${filter.value.length})`
    const baseLabel = `${start}${middle}`
    if (baseLabel.length > MAX_LABEL_LENGTH - indicator.length - 3) {
      const truncated = baseLabel.slice(0, MAX_LABEL_LENGTH - indicator.length - 3)
      return `${truncated}... ${indicator}`
    }
    return `${baseLabel}${indicator}`
  }

  // Truncation for "Contains"
  if (filter.comparison === 'Contains') {
    const prefix = `${start}${middle}`
    const valueStr = String(filter.value)
    const availableSpace = MAX_LABEL_LENGTH - prefix.length - 4
    if (valueStr.length > availableSpace) {
      return `${prefix}${valueStr.slice(0, availableSpace)}...`
    }
    return `${prefix}${valueStr}`
  }

  // General truncation
  if (fullLabel.length > MAX_LABEL_LENGTH) {
    return `${fullLabel.slice(0, MAX_LABEL_LENGTH - 3)}...`
  }

  return fullLabel
}

/**
 * Reactive composable that returns computed label parts for a single filter.
 */
export function useFilterChipLabel(
  filter: Ref<JobFilter | null | undefined>,
  property: Ref<JobFilterProperty | null | undefined>,
) {
  const labelStart = computed(() =>
    filter.value ? buildLabelStart(filter.value, property.value) : '',
  )

  const labelMiddle = computed(() => (filter.value ? buildLabelMiddle(filter.value) : ''))

  const labelEnd = computed(() => (filter.value ? buildLabelEnd(filter.value) : ''))

  const fullLabel = computed(() =>
    filter.value ? buildFullLabel(filter.value, property.value) : '',
  )

  const truncatedLabel = computed(() =>
    filter.value ? buildTruncatedLabel(filter.value, property.value) : '',
  )

  return { labelStart, labelMiddle, labelEnd, fullLabel, truncatedLabel }
}
